import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Loader2, Check, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = `/api`;

export default function CheckOut() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefillId = searchParams.get("event_id") || "";

  // Steps: identify → confirm → survey → thankyou
  const [step, setStep] = useState("identify");
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState(prefillId);
  const [user, setUser] = useState(null);

  // Survey state (legacy, unchanged)
  const [surveyStep, setSurveyStep] = useState(0);
  const [survey, setSurvey] = useState({
    q1_food: null,
    q3_feedback: null,
    q4_frequency: null,
    q5_suggestions: "",
  });

  // Dynamic survey state (used only when the event has feedback_questions)
  const [dynamicStep, setDynamicStep] = useState(0);
  const [responses, setResponses] = useState({});

  const handleFind = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) {
      toast.error("Please enter your Event ID or contact information");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/find-user`, { identifier: identifier.trim() });
      setUser(res.data);
      setStep("confirm");
    } catch (err) {
      const msg = err.response?.data?.detail || "User not found";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const isDynamic = Boolean(user?.feedback_questions && user.feedback_questions.length > 0);

  const handleStartSurvey = () => {
    setStep("survey");
    setSurveyStep(0);
    setDynamicStep(0);
    setResponses({});
  };

  const handleExit = async () => {
    setLoading(true);
    try {
      if (isDynamic) {
        await axios.post(`${API}/exit`, {
          event_id: user.event_id,
          responses,
        });
      } else {
        await axios.post(`${API}/exit`, {
          event_id: user.event_id,
          survey,
        });
      }
      setStep("thankyou");
    } catch (err) {
      const msg = err.response?.data?.detail || "Exit failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const setResponseValue = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  const isDynamicQuestionAnswered = (q) => {
    const val = responses[q.id];
    if (q.type === "yesno") {
      return Boolean(val && val.answer);
    }
    if (q.type === "text") {
      return true; // gating for text handled by `required` below, blank is allowed to type through
    }
    return val !== undefined && val !== null && val !== "";
  };

  const canAdvanceDynamic = () => {
    const q = user.feedback_questions[dynamicStep];
    if (!q.required) return true;
    if (q.type === "text") {
      const val = responses[q.id];
      return Boolean(val && val.trim());
    }
    if (q.type === "yesno") {
      const val = responses[q.id];
      if (!val || !val.answer) return false;
      const trig = q.comment?.trigger || "always";
      const shouldShowComment = q.comment?.enabled && (
        trig === "always" || (trig === "yes" && val.answer === "Yes") || (trig === "no" && val.answer === "No")
      );
      if (shouldShowComment && q.comment?.required) {
        return Boolean(val.comment && val.comment.trim());
      }
      return true;
    }
    return isDynamicQuestionAnswered(q);
  };

  const handleDynamicNext = () => {
    if (dynamicStep < user.feedback_questions.length - 1) {
      setDynamicStep(dynamicStep + 1);
    } else {
      handleExit();
    }
  };

  const canAdvanceSurvey = () => {
    switch (surveyStep) {
      case 0: return survey.q1_food !== null;
      case 1: return survey.q3_feedback !== null;
      case 2: return survey.q4_frequency !== null;
      case 3: return true; // suggestions optional
      default: return false;
    }
  };

  const handleSurveyNext = () => {
    if (surveyStep < 3) {
      setSurveyStep(surveyStep + 1);
    } else {
      handleExit();
    }
  };

  // --- THANK YOU ---
  if (step === "thankyou") {
    return (
      <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
        <div className="flex-1 flex flex-col justify-center">
          <div className="animate-fade-in">
            <div className="w-16 h-16 bg-[var(--brand)] flex items-center justify-center mb-6">
              <Check className="w-8 h-8 text-white" />
            </div>
            <p className="overline text-[var(--brand)] mb-3" data-testid="thankyou-overline">
              Exit Recorded
            </p>
            <h1
              className="text-3xl sm:text-4xl font-black tracking-tight text-[#0A0A0A] leading-tight mb-3"
              data-testid="thankyou-heading"
            >
              Thank you, {user?.name}
            </h1>
            <p className="text-base text-[#525252] leading-relaxed mb-8" data-testid="thankyou-message">
              We're glad to have had you at the event. Your feedback helps us make future events even better.
            </p>
          </div>

          <div className="swiss-card p-6 animate-fade-in animate-delay-2" data-testid="thankyou-card">
            <p className="text-sm text-[#525252] mb-1">Event ID</p>
            <p className="font-mono text-lg font-bold text-[#0A0A0A]">{user?.event_id}</p>
          </div>
        </div>

        <div className="pt-6 animate-fade-in animate-delay-3">
          <Button
            onClick={() => navigate("/")}
            className="w-full h-12 btn-brand rounded-none text-sm"
            data-testid="thankyou-done-btn"
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  const renderDynamicSurvey = () => {
    const q = user.feedback_questions[dynamicStep];
    const total = user.feedback_questions.length;
    const val = responses[q.id];

    return (
      <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center gap-2 mb-4">
            {user.feedback_questions.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 transition-colors duration-300 ${i <= dynamicStep ? "bg-[var(--brand)]" : "bg-black/10"}`}
                data-testid={`survey-progress-${i}`}
              />
            ))}
          </div>
          <p className="overline text-[#525252]">
            Question {dynamicStep + 1} of {total}
          </p>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="animate-fade-in" data-testid={`survey-dynamic-${q.id}`}>
            <h2 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight">
              {q.title}
            </h2>
            {q.subtitle && <p className="text-sm text-[#525252] mb-8">{q.subtitle}</p>}

            {q.type === "rating" && (
              <div className="flex gap-3">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    onClick={() => setResponseValue(q.id, n)}
                    className={`rating-btn ${val === n ? "active" : ""}`}
                    data-testid={`dynamic-rating-${n}`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}

            {q.type === "yesno" && (
              <>
                <div className="flex gap-3 mb-6">
                  {["Yes", "No"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setResponseValue(q.id, { answer: opt, comment: val?.comment || "" })}
                      className={`chip ${val?.answer === opt ? "active" : ""}`}
                      data-testid={`dynamic-yesno-${opt.toLowerCase()}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                {q.comment?.enabled && val?.answer && (
                  q.comment.trigger === "always" ||
                  (q.comment.trigger === "yes" && val.answer === "Yes") ||
                  (q.comment.trigger === "no" && val.answer === "No")
                ) && (
                  <div className="animate-fade-in">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
                      {q.comment.label || "Comment"}
                    </Label>
                    <Textarea
                      placeholder={q.comment.placeholder || ""}
                      value={val?.comment || ""}
                      onChange={(e) => setResponseValue(q.id, { answer: val.answer, comment: e.target.value })}
                      className="min-h-[80px] rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 text-base resize-none"
                      data-testid="dynamic-yesno-comment"
                    />
                  </div>
                )}
              </>
            )}

            {q.type === "choice" && (
              <div className="flex flex-wrap gap-3">
                {(q.options || []).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setResponseValue(q.id, opt)}
                    className={`chip ${val === opt ? "active" : ""}`}
                    data-testid={`dynamic-choice-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}

            {q.type === "text" && (
              <Textarea
                placeholder={q.placeholder || ""}
                value={val || ""}
                onChange={(e) => setResponseValue(q.id, e.target.value)}
                className="min-h-[120px] rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 text-base resize-none"
                data-testid="dynamic-text"
              />
            )}
          </div>

          <div className="pt-8 flex gap-3">
            {dynamicStep > 0 && (
              <Button
                variant="outline"
                onClick={() => setDynamicStep(dynamicStep - 1)}
                className="h-12 px-6 rounded-none border-black/20 text-sm uppercase tracking-wider"
                data-testid="survey-back-btn"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleDynamicNext}
              disabled={!canAdvanceDynamic() || loading}
              className="flex-1 h-12 btn-brand rounded-none text-sm"
              data-testid="survey-next-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
              ) : dynamicStep === total - 1 ? (
                "Submit & Exit"
              ) : (
                <>Next <ChevronRight className="w-4 h-4" /></>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderLegacySurvey = () => (
    <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
      {/* Progress */}
      <div className="mb-8 animate-fade-in">
        <div className="flex items-center gap-2 mb-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-1 flex-1 transition-colors duration-300 ${i <= surveyStep ? "bg-[var(--brand)]" : "bg-black/10"
                }`}
              data-testid={`survey-progress-${i}`}
            />
          ))}
        </div>
        <p className="overline text-[#525252]">
          Question {surveyStep + 1} of 4
        </p>
      </div>

      <div className="flex-1 flex flex-col">
        {/* Q1: Food Rating */}
        {surveyStep === 0 && (
          <div className="animate-fade-in" data-testid="survey-q1">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight">
              How was the food?
            </h2>
            <p className="text-sm text-[#525252] mb-8">Rate from 1 (poor) to 5 (excellent)</p>
            <div className="flex gap-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setSurvey({ ...survey, q1_food: n })}
                  className={`rating-btn ${survey.q1_food === n ? "active" : ""}`}
                  data-testid={`q1-rating-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Q3: Overall Feedback */}
        {surveyStep === 1 && (
          <div className="animate-fade-in" data-testid="survey-q3">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight">
              Overall event feedback
            </h2>
            <p className="text-sm text-[#525252] mb-8">Rate from 1 (poor) to 5 (excellent)</p>
            <div className="flex gap-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  onClick={() => setSurvey({ ...survey, q3_feedback: n })}
                  className={`rating-btn ${survey.q3_feedback === n ? "active" : ""}`}
                  data-testid={`q3-rating-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Q4: Frequency */}
        {surveyStep === 2 && (
          <div className="animate-fade-in" data-testid="survey-q4">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight">
              Event frequency preference
            </h2>
            <p className="text-sm text-[#525252] mb-8">
              In what frequency would you like the chapter to repeat event of similar types?
            </p>
            <div className="flex flex-wrap gap-3">
              {["Annual", "Biennial"].map((opt) => (
                <button
                  key={opt}
                  onClick={() => setSurvey({ ...survey, q4_frequency: opt })}
                  className={`chip ${survey.q4_frequency === opt ? "active" : ""}`}
                  data-testid={`q4-frequency-${opt.toLowerCase().replace(' ', '-')}`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Q5: Suggestions */}
        {surveyStep === 3 && (
          <div className="animate-fade-in" data-testid="survey-q5">
            <h2 className="text-xl sm:text-2xl font-bold text-[#0A0A0A] mb-2 tracking-tight">
              Any suggestions?
            </h2>
            <p className="text-sm text-[#525252] mb-8">
              Any further suggestions which could have made this event better? (Optional)
            </p>
            <Textarea
              placeholder="Share your thoughts..."
              value={survey.q5_suggestions}
              onChange={(e) => setSurvey({ ...survey, q5_suggestions: e.target.value })}
              className="min-h-[120px] rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 text-base resize-none"
              data-testid="q5-suggestions-textarea"
            />
          </div>
        )}

        {/* Navigation */}
        <div className="pt-8 flex gap-3">
          {surveyStep > 0 && (
            <Button
              variant="outline"
              onClick={() => setSurveyStep(surveyStep - 1)}
              className="h-12 px-6 rounded-none border-black/20 text-sm uppercase tracking-wider"
              data-testid="survey-back-btn"
            >
              Back
            </Button>
          )}
          <Button
            onClick={handleSurveyNext}
            disabled={!canAdvanceSurvey() || loading}
            className="flex-1 h-12 btn-brand rounded-none text-sm"
            data-testid="survey-next-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
            ) : surveyStep === 3 ? (
              "Submit & Exit"
            ) : (
              <>Next <ChevronRight className="w-4 h-4" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );

  // --- SURVEY ---
  if (step === "survey") {
    return isDynamic ? renderDynamicSurvey() : renderLegacySurvey();
  }

  // --- CONFIRM USER ---
  if (step === "confirm" && user) {
    return (
      <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
        <div className="flex-1 flex flex-col justify-center">
          <div className="animate-fade-in">
            <p className="overline text-[var(--brand)] mb-3" data-testid="confirm-overline">
              Check Out
            </p>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-[#0A0A0A] leading-tight mb-8">
              Is this you?
            </h1>
          </div>

          <div className="swiss-card p-6 mb-6 animate-fade-in animate-delay-1" data-testid="user-details-card">
            <div className="space-y-4">
              <div>
                <p className="overline text-[#525252] mb-1">Name</p>
                <p className="text-lg font-bold text-[#0A0A0A]" data-testid="confirm-name">{user.name}</p>
              </div>
              <div>
                <p className="overline text-[#525252] mb-1">Event ID</p>
                <p className="font-mono text-lg font-bold text-[#0A0A0A]" data-testid="confirm-event-id">{user.event_id}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-[#525252] mb-1">Contact Number</p>
                <p className="text-sm font-semibold text-[#0A0A0A]" data-testid="confirm-contact">{user.contact}</p>
              </div>
              <div>
                <p className="overline text-[#525252] mb-1">Checked In</p>
                <p className="text-sm text-[#0A0A0A] font-mono" data-testid="confirm-entry-time">
                  {new Date(user.entry_time).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-6 space-y-3 animate-fade-in animate-delay-2">
          <Button
            onClick={handleStartSurvey}
            className="w-full h-12 btn-brand rounded-none text-sm"
            data-testid="exit-event-btn"
          >
            Exit Event
          </Button>
          <Button
            variant="outline"
            onClick={() => { setStep("identify"); setUser(null); setIdentifier(""); }}
            className="w-full h-12 rounded-none border-black/20 text-sm uppercase tracking-wider"
            data-testid="not-me-btn"
          >
            That's not me
          </Button>
        </div>
      </div>
    );
  }

  // --- IDENTIFY ---
  return (
    <div className="min-h-screen flex flex-col px-6 py-8 max-w-lg mx-auto w-full">
      {/* Header */}
      <div className="mb-8 animate-fade-in">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors mb-6"
          data-testid="checkout-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <p className="overline text-[var(--brand)] mb-3">Check Out</p>
        <div className="mb-4">
          <img
            src="/EO Odisha Logo.png"
            alt="EO Odisha Logo"
            className="h-16 w-auto object-contain"
          />
        </div>
        <p className="text-sm text-[#525252] mt-2">
          Enter your Event ID or Contact Number to check out
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleFind} className="flex-1 flex flex-col">
        <div className="animate-fade-in animate-delay-1 space-y-6">
          <div>
            <Label htmlFor="identifier" className="text-xs font-semibold uppercase tracking-wider text-[#525252] mb-2 block">
              Event ID or Contact Number
            </Label>
            <Input
              id="identifier"
              type="text"
              maxLength={15}
              placeholder="e.g. EO-123456 or 9876543210"
              value={identifier}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setIdentifier(value);
              }}
              className="h-12 rounded-none border-black/20 bg-transparent focus-visible:ring-2 focus-visible:ring-[var(--brand)] focus-visible:ring-offset-2 text-base"
              data-testid="checkout-identifier-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 btn-brand rounded-none text-sm"
            data-testid="checkout-find-btn"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Searching...</>
            ) : (
              "Continue"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
